<template>
    <div class="categoryItemContent container">
        <promotion-slider :slides-per-view="3"/>

        <div class="section-listGame__header">
            <div class="title">
                <img :src="categoryImage" :alt="categoryTitle" v-show="categoryImage">
                <div class="title__text">
                    <h2>{{ categoryTitle }}</h2>
                    <p>{{ categoryDescription }}</p>
                </div>
            </div>

            <div class="link" v-show="!showAllGamesMode && totalGamesCount">
                <router-link :to="allGamesUrl">
                    <span>{{ $t('front.game.button.see_all') }} {{ totalGamesCount }}</span>
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 11L6 6L1 1" stroke="#926D20"/>
                    </svg>
                </router-link>
            </div>
        </div>

        <catalog-bar-with-provider-filter :sort-options="sortOptions" :vendor-platform-ids="vendorPlatformIds"
                                          @sortOptionChanged="sortGames" @platformFilterChanged="filterGames"
                                          :key="$route.path"/>

        <div v-if="sortedCategoryGames.length">
            <games-block-with-winners-list :items="blockWithWinnersGames" v-if="winnerListEnabled"/>
            <games-block-simple :items="blockWithWinnersGames" v-else/>

            <component class="sectionGameList" :is="component" :items="getComponentItems(index)"
                       v-for="(component, index) in cycleComponents"
                       :key="index"></component>

            <div class="btn-loadCatalog" @click="showMoreGames()" v-show="!showAllGamesMode && hasMoreGames">
                <span>{{ $t('front.game.button.load_more') }}</span>
            </div>
        </div>

        <div v-show="!loading && !sortedCategoryGames.length" class="text-center mt-5 mb-4">
            <h3>{{ $t('front.game.info.no_one_game_found') }}</h3>
        </div>
    </div>
</template>

<script>
import screenResolution from "../../../../mixins/screenResolution";
import {loadMoreGamesBlocksCount} from "../../../../../config/game";
import appConfig from "../../../../../config/app";
import apiData from "../../../../mixins/apiData";
import categoryGame from "../../../../mixins/categoryGame";
import categoryGamesData from "../../../../mixins/categoryGamesData";
import sortGame from "../../../../mixins/sortGame";
import { virtualCategoryInterface} from "../../../../interfaces/virtualCategoryInterface";
import {categoryGameSortOptions} from "../../../../interfaces/sortGameInterface";

export default {
    name: "CategoryItemContent",

    mixins: [screenResolution, apiData, categoryGame, categoryGamesData, sortGame],

    data: function () {
        return {
            screenWidth: undefined,
            cycleComponents: [
                'GamesBlockWithBigLeftItem',
                'GamesBlockWithBigRightItem',
            ],
            sortedOptionValue: undefined,
            sortOptions: this.getSortOptions(categoryGameSortOptions),
        };
    },

    metaInfo() {
        let metaData = {};

        if (this.showAllGamesMode) {
            let query = Object.assign({}, this.$route.query);
            delete query.show;

            let canonicalRoute = this.$router.resolve({
                query: query,
            });

            metaData.link = [
                {
                    rel: 'canonical',
                    href: `${appConfig.appUrl}${decodeURIComponent(canonicalRoute.resolved.fullPath)}`,
                    vmid: 'canonical',
                }
            ];
        }

        return metaData;
    },

    serverPrefetch() {
        return this.fetchData();
    },

    mounted() {
        this.fetchData();
    },

    watch: {
        showAllGamesMode() {
            this.resetData();
            this.fetchGames();
        },

        user(user, oldUser) {
            // reload games on change user (apply restrictions)
            if ((user && !oldUser || !user && oldUser)) {
                this.resetData();
                this.fetchGames();
            }
        }
    },

    computed: {
        category() {
            return this.$store.state.gamesCategories.slug[this.categorySlug];
        },

        categoryId() {
            return this.category && this.category.id;
        },

        virtualCategoryInterfaceId() {
            return this.category && this.category.virtualCategoryInterfaceId;
        },

        showAllGamesMode() {
            return this.$route.query.show === 'all';
        },

        allGamesUrl() {
            let query = Object.assign({}, this.$route.query);
            query.show = 'all';

            let seeAllRoute = this.$router.resolve({
                query: query,
            });

            return decodeURIComponent(seeAllRoute.resolved.fullPath);
        },

        loadMoreBlocksCount() {
            let screenResolutionName = this.getCurrentScreenResolutionName(this.screenWidth);
            return loadMoreGamesBlocksCount[screenResolutionName] || 3;
        },

        locale() {
            return this.$store.state.locale;
        },

        user() {
            return this.$store.state.user;
        },

        categorySlug() {
            return this.$route.params.slug;
        },

        categoryTitle() {
            return this.category && this.category.title;
        },

        categoryDescription() {
            return this.category && this.category.description;
        },

        categoryImage() {
            return  this.category && this.category.image;
        },

        loadedGames() {
            let loadedGames = this.games.concat(this.loadMoreGames);

            // todo move filtering to back
            if (this.filteredPlatformIds.length && this.virtualCategoryInterfaceId === virtualCategoryInterface.RECOMMENDED_GAMES) {
                // filter for recommended games
                return loadedGames.filter(game => this.filteredPlatformIds.includes(game.platformId));
            } else {
                // filtered on backend
                return loadedGames;
            }
        },

        sortedCategoryGames() {
            if (this.sortedOptionValue) {
                return this.sortGamesBySortOption(this.loadedGames, this.sortedOptionValue);
            } else {
                return this.loadedGames;
            }
        },

        blockWithWinnersGames() {
            return this.sortedCategoryGames.slice(0, this.blockWithWinnersGamesCount);
        },

        cycleComponentsGames() {
            return this.sortedCategoryGames.slice(this.blockWithWinnersGames.length);
        },

        blockWithWinnersGamesCount() {
            // for server side
            if (!this.screenWidth) {
                return this.isDeviceMobile ? this.winnerListEnabled ? 6 : 8 : this.winnerListEnabled ? 8 : 10;
            }

            if (this.screenWidth > 1024) {
                return this.winnerListEnabled ? 8 : 10;
            } else if (this.screenWidth > 600) {
                return this.winnerListEnabled ? 6 : 8;
            } else {
                return 4;
            }
        },

        cycleComponentGamesCount() {
            // for server side
            if (!this.screenWidth) {
                return this.isDeviceMobile ? 6 : 7;
            }

            if (this.screenWidth > 1024) {
                return 7;
            } else if (this.screenWidth > 767) {
                return 8;
            } else if (this.screenWidth > 600) {
                return 6;
            } else {
                return 4;
            }
        },

        startGamesCount() {
            return this.showAllGamesMode ? null : (this.blockWithWinnersGamesCount + this.cycleComponentGamesCount * 2);
        },

        loadMoreGamesCount() {
            return this.blockWithWinnersGamesCount + (this.cycleComponents.length + this.loadMoreBlocksCount) * this.cycleComponentGamesCount - this.loadedGames.length;
        },

        missingCycleComponentsCount() {
            return Math.ceil((this.loadedGames.length - (this.blockWithWinnersGamesCount + this.cycleComponentGamesCount * this.cycleComponents.length)) / this.cycleComponentGamesCount);
        },

        winnerListEnabled() {
            return appConfig.features.winnerList;
        },
    },

    methods: {
        resetData() {
            this.cycleComponents = this.cycleComponents.slice(0, 2);
            this.loadMoreGames = [];
        },

        fetchData() {
            return new Promise(((resolve, reject) => {
                this.$store.dispatch('FETCH_GAME_CATEGORY_ITEM_BY_SLUG', this.createApiData({
                    slug: this.categorySlug,
                }))
                    .then(() => {
                        this.category && this.fetchGames().then(() => resolve()).catch(() => reject());
                    })
                    .catch(error => reject(error));
            }));
        },

        fetchGames(mustReload) {
            return new Promise((resolve, reject) => {
                let data = this.createApiData(this.createFetchGamesData(this.startGamesCount, 0, mustReload));

                this.$store.dispatch('FETCH_CATEGORY_GAMES_BY_ID', data)
                    .then(gamesData => {
                        this.setGamesData(gamesData, false, true);

                        if (this.showAllGamesMode) {
                            let addingCycleComponentsCount = Math.ceil((this.loadedGames.length - this.blockWithWinnersGamesCount) / this.cycleComponentGamesCount) - 2;

                            this.addCycleComponents(addingCycleComponentsCount);
                        }

                        resolve();
                    })
                    .catch(error => reject(error));
            });
        },

        getComponentItems(LoopIndex) {
            let startItem = LoopIndex * this.cycleComponentGamesCount;
            let endItem = (LoopIndex + 1) * this.cycleComponentGamesCount;

            return this.cycleComponentsGames.slice(startItem, endItem);
        },

        showMoreGames() {
            if (this.loadMoreGamesCount > 0) {
                this.fetchLoadMoreCategoryGames(true)
                    .then(() => {
                        if (this.virtualCategoryInterfaceId === virtualCategoryInterface.RECOMMENDED_GAMES) {
                            this.missingCycleComponentsCount > 0 && this.addCycleComponents(this.missingCycleComponentsCount);
                        } else {
                            this.addCycleComponents(this.loadMoreBlocksCount);
                        }
                    });
            } else {
                this.addCycleComponents(this.loadMoreBlocksCount);
            }
        },

        addCycleComponents(count) {
            let oddGamesBlockComponentName = 'GamesBlockWithBigLeftItem';
            let evenGamesBlockComponentName = 'GamesBlockWithBigRightItem';

            for (let i = 0; i < count; i++) {
                let blockComponentName = this.cycleComponents.length % 2 === 0 ? oddGamesBlockComponentName : evenGamesBlockComponentName;
                this.cycleComponents.push(blockComponentName);
            }
        },

        sortGames(sortOptionValue) {
            this.sortedOptionValue = sortOptionValue;
        },

        filterGames(filteredPlatformIds) {
            if (this.virtualCategoryInterfaceId === virtualCategoryInterface.RECOMMENDED_GAMES) {
                // filter on front (not back) for recommended games
                this.filteredPlatformIds = filteredPlatformIds;
            } else {
                this.filteredPlatformIds = filteredPlatformIds;

                this.resetData();
                this.fetchGames(true);
            }
        },
    },
}
</script>

<style lang="sass" scoped>
@import "sass/components/pages/parts/target/CategoryItemContent"
</style>